import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import Modal from "@/components/ui/modal";
import Button from "@/components/ui/button";
import Dropdown from "@/components/ui/dropdown";
import {
  Close as CloseIcon,
  Employees as EmployeesIcon,
} from "@/components/icons";
import Tooltip from "@/components/ui/tooltip";
import type { Staff } from "../types/staff-management.types";
import { educationManagementApi } from "@/features/education-management/services/education-management.api";
import { useStore } from "@/store";
import styles from "./TrainingAssignmentModal.module.scss";

const EDUCATION_PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

interface QuotaInfo {
  requestedEnrollmentsCount: number;
  capacity: number;
}

interface TrainingAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedStaff: Staff[];
  onConfirm?: (staffIds: string[], trainingId: string) => void;
}

const TrainingAssignmentModal: React.FC<TrainingAssignmentModalProps> = ({
  isOpen,
  onClose,
  selectedStaff,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const currentUser = useStore((s) => s.currentUser);
  const [personnel, setPersonnel] = useState<Staff[]>([]);
  const [selectedTrainingId, setSelectedTrainingId] = useState<string>("");
  const [selectedTrainingLabel, setSelectedTrainingLabel] =
    useState<string>("");
  const [educationOptions, setEducationOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);
  const [educationLoading, setEducationLoading] = useState(false);
  const [quota, setQuota] = useState<QuotaInfo | null>(null);
  const [quotaLoading, setQuotaLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchEducations = useCallback(async (search?: string) => {
    setEducationLoading(true);
    try {
      const { data } = await educationManagementApi.getAll({
        page: 1,
        pageSize: EDUCATION_PAGE_SIZE,
        filters: { search: search?.trim() || undefined },
      });
      setEducationOptions(
        data.map((e) => ({
          label: e.educationName || e.id,
          value: e.id,
        })),
      );
    } catch {
      setEducationOptions([]);
    } finally {
      setEducationLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      setPersonnel([...selectedStaff]);
      setSelectedTrainingId("");
      setSelectedTrainingLabel("");
      setQuota(null);
      fetchEducations();
    }
  }, [isOpen, selectedStaff, fetchEducations]);

  useEffect(() => {
    if (!selectedTrainingId) {
      setQuota(null);
      return;
    }
    let cancelled = false;
    setQuotaLoading(true);
    setQuota(null);
    educationManagementApi
      .getById(selectedTrainingId)
      .then(({ data }) => {
        if (!cancelled)
          setQuota({
            requestedEnrollmentsCount: data.requestedEnrollmentsCount ?? 0,
            capacity: data.capacity ?? 0,
          });
      })
      .catch(() => {
        if (!cancelled) setQuota(null);
      })
      .finally(() => {
        if (!cancelled) setQuotaLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedTrainingId]);

  const handleEducationSearch = useCallback(
    (value: string) => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = setTimeout(() => {
        searchDebounceRef.current = null;
        fetchEducations(value);
      }, SEARCH_DEBOUNCE_MS);
    },
    [fetchEducations],
  );

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  const removePerson = (id: string) => {
    setPersonnel((prev) => prev.filter((p) => p.id !== id));
  };

  const isQuotaFull =
    quota != null &&
    quota.capacity > 0 &&
    quota.requestedEnrollmentsCount >= quota.capacity;
  const wouldExceedQuota =
    quota != null &&
    quota.capacity > 0 &&
    quota.requestedEnrollmentsCount + personnel.length > quota.capacity;

  const handleSaveToEducation = async () => {
    if (personnel.length === 0 || !selectedTrainingId) return;
    const reviewerUserId = currentUser?.id;
    if (!reviewerUserId) {
      return;
    }
    setSaveLoading(true);
    try {
      for (const p of personnel) {
        await educationManagementApi.createEnrollment(selectedTrainingId, {
          userId: p.id,
          reviewerUserId,
        });
      }
      onConfirm?.(
        personnel.map((p) => p.id),
        selectedTrainingId,
      );
      onClose();
    } catch {
      // Error toast handled by apiClient interceptor
    } finally {
      setSaveLoading(false);
    }
  };

  const customFooter = (
    <div className={styles.footer}>
      <Button
        variant="contained"
        color="primary"
        className={styles.saveButton}
        onClick={handleSaveToEducation}
        disabled={
          personnel.length === 0 ||
          !selectedTrainingId ||
          saveLoading ||
          !currentUser?.id ||
          wouldExceedQuota
        }
      >
        {saveLoading
          ? t("common.loading")
          : t("staffManagement.trainingAssignment.saveButton")}
      </Button>
    </div>
  );

  return (
    <Modal
      title={t("staffManagement.trainingAssignment.title")}
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      customFooter={customFooter}
    >
      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>
          {t("staffManagement.trainingAssignment.peopleToAssign")}
        </h4>
        <div className={styles.chipsRow}>
          {personnel.map((p) => (
            <div key={p.id} className={styles.chipWrap}>
              <span>{p.name}</span>
              <button
                type="button"
                className={styles.chipRemove}
                onClick={() => removePerson(p.id)}
                aria-label={t("common.close")}
              >
                <CloseIcon width={16} height={16} color="#707A8F" />
              </button>
            </div>
          ))}
        </div>
        <p className={styles.instructionText}>
          {t("staffManagement.trainingAssignment.instruction")}{" "}
          <strong className={styles.instructionCount}>
            {personnel.length} adet
          </strong>{" "}
          {t("staffManagement.trainingAssignment.instructionPersonnel")}
        </p>
      </div>

      <div className={styles.section}>
        <div className={styles.trainingField}>
          <Dropdown
            label={t("staffManagement.trainingAssignment.trainingName")}
            placeholder={
              educationLoading
                ? t("common.loading")
                : t("staffManagement.trainingAssignment.trainingPlaceholder")
            }
            options={educationOptions}
            value={selectedTrainingId}
            selectedLabel={selectedTrainingLabel}
            searchable
            onSearch={handleEducationSearch}
            onSelect={(value) => {
              const id = Array.isArray(value) ? value[0] : value;
              const option = educationOptions.find((o) => o.value === id);
              setSelectedTrainingId(id ?? "");
              setSelectedTrainingLabel(option?.label ?? "");
            }}
          />
        </div>
        {selectedTrainingId && (
          <div className={styles.quotaRow}>
            <span className={styles.quotaIcon}>
              <EmployeesIcon width={20} height={20} color="#707A8F" />
            </span>
            <span className={styles.quotaLabel}>
              {t("staffManagement.trainingAssignment.quota")}:
            </span>
            {quotaLoading ? (
              <span className={styles.quotaValue}>
                {t("common.loading")}
              </span>
            ) : quota != null ? (
              <>
                <span className={styles.quotaValue}>
                  {isQuotaFull || wouldExceedQuota ? (
                    <>
                      <span className={styles.quotaUsed}>
                        {quota.requestedEnrollmentsCount}
                      </span>
                      <span>/{quota.capacity}</span>
                    </>
                  ) : (
                    `${quota.requestedEnrollmentsCount}/${quota.capacity}`
                  )}
                </span>
                {(isQuotaFull || wouldExceedQuota) && (
                  <Tooltip
                    content={t("staffManagement.trainingAssignment.quotaWarningTooltip")}
                    position="top"
                  >
                    <span
                      className={styles.quotaWarningIcon}
                      aria-label={t("staffManagement.trainingAssignment.quotaWarningTooltip")}
                    >
                      i
                    </span>
                  </Tooltip>
                )}
              </>
            ) : null}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default TrainingAssignmentModal;
